import { Autocomplete } from "$components/autocomplete/autocomplete";
import { useSearchEngine } from "$components/autocomplete/use-search-engine";
import { makeStyles } from "$components/mui-styles";
import { CircularProgress, TextField, Typography } from "@material-ui/core";
import type Fuse from "fuse.js";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import type { TranslatedInvestigationCatalogueItem } from "./util";

const FUSE_OPTIONS = {
  keys: [
    {
      name: "keywords",
      weight: 0.3,
    },
    { name: "title", weight: 0.7 },
  ],
};

export const InvestigationSelect = React.forwardRef(
  (
    {
      investigationList,
      value,
      onChange,
      onClear,
      error,
      loading = false,
      ...props
    }: {
      investigationList: TranslatedInvestigationCatalogueItem[];
      value: TranslatedInvestigationCatalogueItem | null;
      onChange: (value: TranslatedInvestigationCatalogueItem | null) => void;
      error: string | null;
      onClear?: () => void;
      loading?: boolean;
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState(value?.title ?? "");
    const { formatMessage } = useIntl();
    const searchEngine = useSearchEngine(investigationList, FUSE_OPTIONS);

    useEffect(() => {
      setInputValue(value?.title ?? "");
    }, [value]);

    return (
      <Autocomplete
        {...props}
        value={value?.title ?? ""}
        inputValue={inputValue}
        onInputChange={(_e, val) => setInputValue(val)}
        noOptionsText={formatMessage({
          id: "investigation.select.noOptions",
          defaultMessage: "Start typing to search for an investigation...",
        })}
        searchEngine={searchEngine}
        getOptionLabel={(option) =>
          (option?.item?.title as unknown as string) ?? ""
        }
        onBlur={() => setInputValue((curr) => value?.title ?? curr)}
        renderOption={(option) => <InvestigationOption option={option} />}
        onChange={(option, reason) => {
          if (reason === "clear" && onClear) {
            onClear();
          } else {
            onChange(option || null);
          }
        }}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={ref}
            autoComplete="off"
            type="text"
            name="investigation"
            label={formatMessage({
              id: "investigation.select.placeholder",
              defaultMessage: "Investigation",
            })}
            error={!!error}
            helperText={error ? "Please select an investigation" : null}
            data-testid="investigation-input"
            InputProps={{
              ...params.InputProps,
              type: "search",
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                      data-testid="investigation-select-loading"
                    />
                  ) : (
                    params.InputProps.endAdornment
                  )}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    );
  },
);

// TODO: ENG-4293 look to rebuild this
const InvestigationOption: FC<{
  option: {
    item?: TranslatedInvestigationCatalogueItem;
    matches?: readonly Fuse.FuseResultMatch[];
  };
}> = ({ option }) => {
  const { item, matches } = option;
  const matchesTitle = matches?.find((match) => match?.key?.endsWith("title"));
  const keywordMatches =
    matches?.filter((match) => !match?.key?.endsWith("title")) ?? [];

  return (
    <SuggestionWrapper>
      <Typography variant="body1">
        <HighlightedString
          string={matchesTitle?.value || item?.title || ""}
          indices={matchesTitle?.indices || []}
        />
      </Typography>
      {keywordMatches.length > 0 && (
        <Typography variant="body1">
          {"("}
          {keywordMatches.map(({ value, indices }, i, arr) => (
            <React.Fragment key={`${value}-${indices.join("-")}`}>
              <HighlightedString string={value ?? ""} indices={indices} />
              {i !== arr.length - 1 && ", "}
            </React.Fragment>
          ))}
          {")"}
        </Typography>
      )}
    </SuggestionWrapper>
  );
};

// TODO: ENG-4293 look to refactor these below components

// TODO: move to parent folder if needed by other care activity components in next milestones
const useSuggestionWrapperStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      display: "inline",
      "&:not(:last-child)": {
        marginRight: theme.spacing(0.5),
      },
    },
    "& mark": {
      backgroundColor: "transparent",
      fontWeight: 600,
    },
  },
}));

export const SuggestionWrapper: FCwC = ({ children }) => {
  const classes = useSuggestionWrapperStyles();
  return (
    <div data-testid="investigation-v2-suggestion" className={classes.root}>
      {children}
    </div>
  );
};

const HighlightedString: FC<{
  string: string;
  indices: readonly Fuse.RangeTuple[];
}> = ({ string, indices }) => {
  const strings: React.ReactNode[] = [];

  // Non-matching part at start of string
  const firstIndex = indices[0];
  if (firstIndex) {
    const [firstMatchStart] = firstIndex;
    const prefix = string.substring(0, firstMatchStart);
    if (prefix) {
      strings.push(prefix);
    }
  } else {
    strings.push(string);
  }

  indices.forEach(([matchStart, matchEnd], i) => {
    // Matching part of string
    const match = string.substring(matchStart, matchEnd + 1);
    strings.push(<mark key={`${matchStart}-${matchEnd}`}>{match}</mark>);

    const nextIndex = indices[i + 1];
    if (nextIndex) {
      // Non-matching part between two matches
      const [nextFrom] = nextIndex;
      const nonMatchPart = string.substring(matchEnd + 1, nextFrom);

      if (nonMatchPart) {
        strings.push(nonMatchPart);
      }

      return;
    }

    // Non-matching part at end of string
    const stringRemainder = string.substring(matchEnd + 1, string.length);
    if (stringRemainder) {
      strings.push(stringRemainder);
    }
  });

  return <>{strings}</>;
};
